import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, Mail, Check, Loader2, ArrowRight, User, PawPrint, 
  Calendar, CreditCard, AlertCircle, CheckCircle, Wallet, Link
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_PLANS = [
  { id: 'quarterly', label: 'Quartal', price: 30, months: 3 },
  { id: 'halfyearly', label: 'Halbjährlich', price: 57, months: 6, saving: 3 },
  { id: 'yearly', label: 'Jährlich', price: 110, months: 12, saving: 10, recommended: true }
];

const MembershipRenewalForm = ({ onClose, renewalToken = null }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [memberData, setMemberData] = useState(null);
  const [renewalData, setRenewalData] = useState({
    paymentPlan: 'yearly',
    autoRenewal: true
  });
  const [tokenVerified, setTokenVerified] = useState(false);

  // Prüfe Token beim Laden
  useEffect(() => {
    if (renewalToken) {
      verifyToken(renewalToken);
    }
  }, [renewalToken]);

  // Token-basierte Verifizierung (aus E-Mail-Link)
  const verifyToken = async (token) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/membership/verify-token`, {
        token: token
      });

      if (response.data.verified) {
        setMemberData(response.data.member);
        setEmail(response.data.member.email);
        setTokenVerified(true);
        // Setze aktuelle autoRenewal-Einstellung als Standard
        if (response.data.member.currentAutoRenewal !== undefined) {
          setRenewalData(prev => ({ ...prev, autoRenewal: response.data.member.currentAutoRenewal }));
        }
        setStep(2); // Direkt zu Schritt 2 (Mitgliedsdaten)
        toast.success('Willkommen zurück! Ihre Daten wurden geladen.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Der Link ist ungültig oder abgelaufen');
      setTokenVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: E-Mail eingeben und Mitgliedschaft prüfen
  const handleCheckMembership = async () => {
    if (!email.trim()) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    setIsLoading(true);
    try {
      // Check if member exists
      const response = await axios.post(`${BACKEND_URL}/api/membership/check-renewal`, {
        email: email.trim().toLowerCase()
      });

      if (response.data.found) {
        // Send verification email
        await axios.post(`${BACKEND_URL}/api/membership/send-renewal-verification`, {
          email: email.trim().toLowerCase()
        });
        setVerificationSent(true);
        toast.success('Verifizierungscode wurde per E-Mail gesendet');
      } else {
        toast.error('Keine aktive Mitgliedschaft mit dieser E-Mail gefunden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Überprüfung');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1b: Verifizierungscode prüfen
  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      toast.error('Bitte geben Sie den Verifizierungscode ein');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/membership/verify-renewal`, {
        email: email.trim().toLowerCase(),
        code: verificationCode.trim()
      });

      if (response.data.verified) {
        setMemberData(response.data.member);
        setStep(2);
        toast.success('E-Mail verifiziert!');
      } else {
        toast.error('Ungültiger Code');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger oder abgelaufener Code');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Verlängerung abschließen
  const handleSubmitRenewal = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/membership/process-renewal`, {
        email: email.trim().toLowerCase(),
        paymentPlan: renewalData.paymentPlan,
        autoRenewal: renewalData.autoRenewal
      });

      if (response.data.success) {
        setStep(4); // Success step
        toast.success('Verlängerung erfolgreich!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Verlängerung');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPlan = PAYMENT_PLANS.find(p => p.id === renewalData.paymentPlan);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-8 w-8" />
              <div>
                <CardTitle className="text-xl">Mitgliedschaft verlängern</CardTitle>
                <CardDescription className="text-green-100">
                  Schritt {step} von 4
                </CardDescription>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Loading state when verifying token */}
          {isLoading && renewalToken && step === 1 && (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 mx-auto text-green-500 animate-spin mb-4" />
              <p className="text-gray-600">Ihre Daten werden geladen...</p>
            </div>
          )}

          {/* Step 1: E-Mail Verifizierung */}
          {step === 1 && !isLoading && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Mail className="h-12 w-12 mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-semibold">E-Mail bestätigen</h3>
                <p className="text-gray-600 text-sm">
                  Geben Sie die E-Mail-Adresse ein, mit der Sie sich registriert haben.
                </p>
              </div>

              {!verificationSent ? (
                <>
                  <div className="space-y-2">
                    <Label>E-Mail-Adresse</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.de"
                      className="text-lg"
                    />
                  </div>

                  <Button
                    onClick={handleCheckMembership}
                    disabled={isLoading || !email.trim()}
                    className="w-full bg-green-500 hover:bg-green-600"
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="h-5 w-5 mr-2" />
                    )}
                    Mitgliedschaft prüfen
                  </Button>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                    <p className="text-green-800 font-medium">
                      Verifizierungscode wurde an {email} gesendet
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>6-stelliger Verifizierungscode</Label>
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest"
                      maxLength={6}
                    />
                  </div>

                  <Button
                    onClick={handleVerifyCode}
                    disabled={isLoading || verificationCode.length !== 6}
                    className="w-full bg-green-500 hover:bg-green-600"
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Check className="h-5 w-5 mr-2" />
                    )}
                    Code bestätigen
                  </Button>

                  <button
                    onClick={() => setVerificationSent(false)}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Andere E-Mail-Adresse verwenden
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 2: Mitgliedsdaten anzeigen */}
          {step === 2 && memberData && (
            <div className="space-y-6">
              {tokenVerified && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                  <Link className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-700">
                    <strong>Verifiziert über E-Mail-Link</strong> – Sie wurden automatisch angemeldet.
                  </p>
                </div>
              )}
              
              <div className="text-center mb-4">
                <User className="h-12 w-12 mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-semibold">Ihre Mitgliedschaft</h3>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mitgliedsnummer:</span>
                  <span className="font-bold text-green-600">#{memberData.memberNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{memberData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vertragsende:</span>
                  <span className="font-medium text-orange-600">
                    {memberData.contractEndDate ? new Date(memberData.contractEndDate).toLocaleDateString('de-DE') : 'Nicht festgelegt'}
                  </span>
                </div>
              </div>

              {memberData.pets && memberData.pets.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <PawPrint className="h-4 w-4" /> Registrierte Tiere
                  </Label>
                  <div className="bg-blue-50 rounded-lg p-3">
                    {memberData.pets.map((pet, index) => (
                      <div key={index} className="flex items-center gap-2 py-1">
                        <span className="text-blue-600">•</span>
                        <span>{pet.petName} ({pet.petType})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-medium">Hinweis zur Verlängerung</p>
                    <p className="text-amber-700 text-sm mt-1">
                      Bei der Verlängerung wird ein neuer Vertrag erstellt. Ihre Mitgliedsnummer und registrierten Tiere bleiben erhalten.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setStep(3)}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Weiter zur Verlängerung
              </Button>
            </div>
          )}

          {/* Step 3: Verlängerungsoptionen */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <Calendar className="h-12 w-12 mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-semibold">Verlängerung konfigurieren</h3>
              </div>

              {/* Zahlungsplan */}
              <div className="space-y-3">
                <Label>Zahlungsplan wählen</Label>
                <div className="grid gap-3">
                  {PAYMENT_PLANS.map((plan) => (
                    <label
                      key={plan.id}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        renewalData.paymentPlan === plan.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentPlan"
                          checked={renewalData.paymentPlan === plan.id}
                          onChange={() => setRenewalData(prev => ({ ...prev, paymentPlan: plan.id }))}
                          className="h-5 w-5 text-green-600"
                        />
                        <div>
                          <span className="font-medium">{plan.label}</span>
                          {plan.saving && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              {plan.saving}€ sparen
                            </span>
                          )}
                          {plan.recommended && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Empfohlen
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-lg">{plan.price}€</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Automatische Verlängerung */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" /> Vertragsverlängerung
                </Label>
                
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    renewalData.autoRenewal === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="autoRenewal"
                    checked={renewalData.autoRenewal === true}
                    onChange={() => setRenewalData(prev => ({ ...prev, autoRenewal: true }))}
                    className="mt-1 h-5 w-5 text-blue-600"
                  />
                  <div>
                    <span className="font-medium">Automatische Verlängerung</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Verlängert sich automatisch, wenn nicht 4 Wochen vorher gekündigt.
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    renewalData.autoRenewal === false
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="autoRenewal"
                    checked={renewalData.autoRenewal === false}
                    onChange={() => setRenewalData(prev => ({ ...prev, autoRenewal: false }))}
                    className="mt-1 h-5 w-5 text-orange-600"
                  />
                  <div>
                    <span className="font-medium">Keine automatische Verlängerung</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Vertrag endet automatisch nach einem Jahr.
                    </p>
                  </div>
                </label>
              </div>

              {/* Zusammenfassung */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <h4 className="font-semibold text-gray-700">Zusammenfassung:</h4>
                <div className="flex justify-between">
                  <span>Zahlungsplan:</span>
                  <span className="font-medium">{selectedPlan?.label}</span>
                </div>
                <hr />
                <div className="flex justify-between text-lg font-bold">
                  <span>Gesamtbetrag:</span>
                  <span className="text-green-600">{selectedPlan?.price}€</span>
                </div>
              </div>

              {/* Zahlungshinweis */}
              <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
                <Wallet className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Zahlung per Überweisung</p>
                  <p className="text-sm text-blue-600">Sie erhalten die Bankdaten per E-Mail.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  Zurück
                </Button>
                <Button
                  onClick={handleSubmitRenewal}
                  disabled={isLoading}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Check className="h-5 w-5 mr-2" />
                  )}
                  Verlängerung abschließen
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Erfolg */}
          {step === 4 && (
            <div className="text-center space-y-6 py-6">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-green-600 mb-2">
                  Verlängerung erfolgreich!
                </h3>
                <p className="text-gray-600">
                  Ihre Pfotenflatrate wurde erfolgreich verlängert.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h4 className="font-semibold text-green-800 mb-2">Nächste Schritte:</h4>
                <ol className="text-sm text-green-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="bg-green-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <span>Sie erhalten eine E-Mail mit Ihren neuen Vertragsunterlagen und den Bankdaten.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-green-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <span>Überweisen Sie den Betrag mit Ihrer Mitgliedsnummer als Verwendungszweck.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-green-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <span>Nach Zahlungseingang ist Ihre verlängerte Mitgliedschaft sofort aktiv.</span>
                  </li>
                </ol>
              </div>

              <Button onClick={onClose} className="w-full">
                Schließen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipRenewalForm;
